import { Link } from 'react-router-dom';
import { Zap, Mail, MapPin, Phone, ChevronRight, ExternalLink } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="relative bg-[#0A0A0A] border-t border-white/10">
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg opacity-10" />
      
      {/* Main Footer Content */}
      <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 group mb-6">
              <div className="w-12 h-12 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-6 h-6 text-[#00F0FF]" />
              </div>
              <div>
                <span className="font-heading text-xl font-bold uppercase tracking-wide text-white">ECU Flash</span>
                <p className="text-xs text-[#888]">Service</p>
              </div>
            </Link>
            <p className="text-[#888] text-sm leading-relaxed mb-6">
              Professional ECU tuning and modification services. Fast turnaround, all brands supported, guaranteed results.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href="mailto:support@ecuflash.com" className="flex items-center gap-3 text-[#888] hover:text-[#00F0FF] transition-colors text-sm">
                <Mail className="w-4 h-4" />
                support@ecuflash.com
              </a>
              <div className="flex items-center gap-3 text-[#888] text-sm">
                <MapPin className="w-4 h-4" />
                Fiji Islands
              </div>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#00F0FF] mb-6">Services</h4>
            <ul className="space-y-3">
              {[
                { to: '/services/dtc-removal', label: 'DTC Removal' },
                { to: '/services/dpf-off', label: 'DPF Delete' },
                { to: '/services/egr-off', label: 'EGR Delete' },
                { to: '/services/adblue-off', label: 'AdBlue Delete' },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="group flex items-center text-[#888] hover:text-white transition-colors text-sm"
                  >
                    <ChevronRight className="w-4 h-4 mr-2 text-[#00F0FF] opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#00F0FF] mb-6">Resources</h4>
            <ul className="space-y-3">
              {[
                { to: '/tools/dtc-delete', label: 'DTC Delete Tool', highlight: true },
                { to: '/how-it-works', label: 'How It Works' },
                { to: '/pricing', label: 'Pricing' },
                { to: '/faq', label: 'FAQ' },
                { to: '/contact', label: 'Contact' },
                { to: '/about', label: 'About Us' },
                { to: '/portal', label: 'Customer Portal' },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className={`group flex items-center transition-colors text-sm ${
                      link.highlight 
                        ? 'text-[#00F0FF] hover:text-white' 
                        : 'text-[#888] hover:text-white'
                    }`}
                  >
                    <ChevronRight className="w-4 h-4 mr-2 text-[#00F0FF] opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                    {link.highlight && <Zap className="w-3 h-3 ml-2" />}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Learning Center Sub-section */}
            <h4 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#00F0FF] mt-6 mb-4">Learning Center</h4>
            <ul className="space-y-3">
              {[
                { to: '/how-ecu-tuning-works', label: 'How ECU Tuning Works' },
                { to: '/vehicle-tuning-guides', label: 'Vehicle Brand Guides' },
                { to: '/troubleshooting-guide', label: 'Troubleshooting Guide' },
                { to: '/glossary', label: 'ECU Glossary' },
                { to: '/testimonials', label: 'Customer Reviews' },
                { to: '/blog', label: 'Blog & Articles' },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="group flex items-center text-[#888] hover:text-white transition-colors text-sm"
                  >
                    <ChevronRight className="w-4 h-4 mr-2 text-[#00F0FF] opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#00F0FF] mb-6">Legal</h4>
            <ul className="space-y-3">
              {[
                { to: '/terms', label: 'Terms of Service' },
                { to: '/privacy', label: 'Privacy Policy' },
                { to: '/refund', label: 'Refund Policy' },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="group flex items-center text-[#888] hover:text-white transition-colors text-sm"
                  >
                    <ChevronRight className="w-4 h-4 mr-2 text-[#00F0FF] opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Disclaimer */}
            <div className="mt-6 p-4 bg-[#FF003C]/10 border border-[#FF003C]/30">
              <p className="text-xs text-[#FF003C] font-semibold uppercase tracking-wider">
                Off-Road Use Only
              </p>
              <p className="text-xs text-[#888] mt-1">
                Our services are intended for off-road and competition use only.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 border-t border-white/10">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#888] text-sm">
              © {new Date().getFullYear()} ECU Flash Service. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-xs text-[#888] uppercase tracking-wider">
                Powered by Advanced ECU Technology
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative bottom line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#00F0FF] to-transparent opacity-50" />
    </footer>
  );
};

export default Footer;
